import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalKey = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  updated: 'Última actualización: 3 de junio de 2026',
  sections: [{
    heading: '1. Información que Recopilamos',
    body: 'Cuando adquieres "El Vuelo del Renacer", recopilamos únicamente la información necesaria para procesar tu compra y entregarte el libro digital: tu nombre, dirección de correo electrónico y los datos de pago procesados de forma segura por nuestros proveedores de pago.'
  }, {
    heading: '2. Cómo Usamos Tu Información',
    body: 'Usamos tu información para procesar tu compra, enviarte el libro digital, comunicarte actualizaciones relacionadas con el producto y, si así lo autorizas, enviarte contenido relacionado con el proceso del renacer. Nunca compartimos ni vendemos tus datos a terceros.'
  }, {
    heading: '3. Seguridad de Tus Datos',
    body: 'Tus datos están protegidos mediante encriptación SSL y procesamiento seguro de pagos. Los datos financieros no se almacenan en nuestros servidores — son procesados directamente por plataformas de pago certificadas.'
  }, {
    heading: '4. Tus Derechos',
    body: 'Tienes derecho a acceder, modificar o eliminar tus datos personales en cualquier momento. También puedes darte de baja de cualquier comunicación con un solo clic en el enlace incluido en cada correo.'
  }, {
    heading: '5. Cookies',
    body: 'Este sitio utiliza cookies básicas para mejorar tu experiencia de navegación. No utilizamos cookies de rastreo invasivas ni compartimos información de navegación con terceros con fines publicitarios.'
  }, {
    heading: '6. Cambios a esta Política',
    body: 'Nos reservamos el derecho de actualizar esta política. Cualquier cambio será publicado en esta misma página con la fecha de actualización correspondiente.'
  }]
};
const termsContent = {
  title: 'Términos de Servicio',
  updated: 'Última actualización: 3 de junio de 2026',
  sections: [{
    heading: '1. Aceptación de los Términos',
    body: 'Al adquirir "El Vuelo del Renacer", aceptas estos Términos de Servicio en su totalidad. Si no estás de acuerdo con alguna parte de estos términos, te pedimos que no completes la compra.'
  }, {
    heading: '2. Licencia de Uso',
    body: '"El Vuelo del Renacer" es una obra protegida por derechos de autor. Al comprarlo, recibes una licencia personal e intransferible para leer, estudiar y aplicar su contenido en tu vida. No está permitida la reproducción, distribución, reventa o uso comercial del libro sin autorización expresa por escrito.'
  }, {
    heading: '3. Acceso al Producto',
    body: 'Una vez completado el pago, recibirás acceso inmediato al libro digital a través del correo electrónico proporcionado. El producto es de descarga digital — no se envía material físico.'
  }, {
    heading: '4. Política de Reembolso',
    body: 'Tu compra incluye una garantía: si después de leer los primeros tres capítulos sientes que este libro no es lo que necesitabas, puedes solicitar un reembolso dentro de los 14 días siguientes a tu compra. Una vez procesado el reembolso, se cancelará tu acceso al producto.'
  }, {
    heading: '5. Naturaleza del Contenido',
    body: '"El Vuelo del Renacer" es un libro de crecimiento personal con componente espiritual. No sustituye atención médica, psicológica ni psiquiátrica profesional. Si estás atravesando una crisis emocional o pensamientos de hacerte daño, te pedimos buscar ayuda profesional inmediata.'
  }, {
    heading: '6. Limitación de Responsabilidad',
    body: 'El autor y los responsables del producto no se hacen responsables de las decisiones, interpretaciones o aplicaciones que cada lector haga del contenido. Los resultados varían según el compromiso, las circunstancias y la situación personal de cada lector.'
  }, {
    heading: '7. Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. La versión vigente será siempre la publicada en esta página.'
  }, {
    heading: '8. Ley Aplicable',
    body: 'Estos términos se rigen por las leyes aplicables al lugar de residencia del autor. Cualquier disputa será resuelta de buena fe y, en caso necesario, ante los tribunales competentes.'
  }]
};
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalKey>(null);
  // Close on Escape key
  useEffect(() => {
    if (!openModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openModal]);
  const activeContent = openModal === 'privacy' ? privacyContent : openModal === 'terms' ? termsContent : null;
  return <>
      <footer className="w-full bg-[var(--charcoal)] py-12" data-id="element-144">
        <div className="max-w-6xl mx-auto px-6" data-id="element-145">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-[var(--cream)] text-sm" data-id="element-146">
            <div className="text-center md:text-left" data-id="element-147">
              <p className="font-sans text-base font-semibold uppercase tracking-[0.25em] text-[var(--warm-gold)] mb-1" data-id="element-148">
                El Vuelo del Renacer
              </p>
            </div>

            <div className="flex gap-6" data-id="element-149">
              <button onClick={() => setOpenModal('privacy')} className="hover:text-[var(--warm-gold)] transition-colors" data-id="element-150">
                Política de Privacidad
              </button>
              <button onClick={() => setOpenModal('terms')} className="hover:text-[var(--warm-gold)] transition-colors" data-id="element-151">
                Términos de Servicio
              </button>
            </div>

            <p data-id="element-152">© 2026 Todos los derechos reservados</p>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-153">
        {activeContent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-154">
            {/* Backdrop */}
            <div onClick={() => setOpenModal(null)} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-155" />

            {/* Modal panel */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="relative bg-[var(--cream)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border-2 border-[var(--warm-gold)]/30" data-id="element-156">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 p-6 md:p-8 border-b border-[var(--charcoal)]/10" data-id="element-157">
                <div data-id="element-158">
                  <h3 id="modal-title" className="font-display text-2xl md:text-3xl text-[var(--midnight)]" data-id="element-159">
                    {activeContent.title}
                  </h3>
                  <p className="text-sm text-[var(--charcoal)]/60 mt-1 italic" data-id="element-160">
                    {activeContent.updated}
                  </p>
                </div>
                <button onClick={() => setOpenModal(null)} aria-label="Cerrar" className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--midnight)]/5 hover:bg-[var(--midnight)]/10 flex items-center justify-center text-[var(--charcoal)] transition-colors" data-id="element-161">
                  <X className="w-5 h-5" data-id="element-162" />
                </button>
              </div>

              {/* Body (scrollable) */}
              <div className="overflow-y-auto p-6 md:p-8 space-y-6" data-id="element-163">
                {activeContent.sections.map((section, i) => <div key={i} className="space-y-2" data-id="element-164">
                    <h4 className="font-semibold text-lg text-[var(--midnight)]" data-id="element-165">
                      {section.heading}
                    </h4>
                    <p className="text-[var(--charcoal)] leading-relaxed" data-id="element-166">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              {/* Footer */}
              <div className="p-6 md:p-8 border-t border-[var(--charcoal)]/10 flex justify-end" data-id="element-167">
                <button onClick={() => setOpenModal(null)} className="bg-[var(--warm-gold)] text-[var(--midnight)] px-6 py-3 rounded-full font-semibold hover:scale-[1.03] transition-transform" data-id="element-168">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}